import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, ShoppingBag, Gamepad2, Play, Volume2, VolumeX, RotateCcw, Flame, BookOpen } from "lucide-react";
import { useGameStore, gameStore } from "@/hooks/useGameStore";
import { audio } from "@/game/audio";
import { ASSETS, TARGETS, CROSSBOWS, ARROWS, SKINS } from "@/data/catalog";
import KeyedImage from "@/components/KeyedImage";
import SplashScreen from "@/pages/SplashScreen";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SPLASH_KEY = "crossbow_chaos_splash_session_v1";

export default function HomeScreen() {
  const state = useGameStore();
  const nav = useNavigate();

  const [showSplash, setShowSplash] = useState(() => {
    try { return sessionStorage.getItem(SPLASH_KEY) !== "seen"; } catch { return false; }
  });
  const [resetOpen, setResetOpen] = useState(false);

  // Daily streak bonus once per calendar day
  useEffect(() => {
    // Give the splash a moment before popping a toast
    if (showSplash) return;
    const res = gameStore.checkDailyBonus();
    if (res.bonus > 0) {
      toast.success(`Daily streak Day ${res.streak}! +${res.bonus} coins`, {
        duration: 4000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSplash]);

  const go = (path) => {
    audio.unlock();
    audio.start();
    nav(path);
  };

  const handleSplashDone = () => {
    try { sessionStorage.setItem(SPLASH_KEY, "seen"); } catch {}
    setShowSplash(false);
  };

  const handleReset = () => {
    gameStore.reset();
    setResetOpen(false);
    audio.start();
    toast.success("Progress reset. Fresh start!");
  };

  if (showSplash) return <SplashScreen onDone={handleSplashDone} />;

  return (
    <div
      className="relative w-full h-full arcade-bg flex flex-col items-center justify-between overflow-hidden"
      data-testid="home-screen"
    >
      {/* Top HUD */}
      <div className="w-full flex justify-between items-start p-4 sm:p-6 z-10 safe-top safe-x">
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <div className="hud-pill hud-pill--gold" data-testid="home-coins">
              <Coins className="w-5 h-5 text-[#FFD700]" />
              <span className="neon-yellow text-lg sm:text-xl" data-testid="coin-count">
                {state.coins.toLocaleString()}
              </span>
            </div>
            {state.streak?.count > 0 && (
              <div className="hud-pill hud-pill--pink" data-testid="streak-pill" title="Daily streak">
                <Flame className="w-4 h-4 text-[#FF007F]" />
                <span className="neon-pink text-sm sm:text-base">{state.streak.count}</span>
              </div>
            )}
          </div>
          <div
            className="pl-2 flex items-center gap-2 text-[10px] sm:text-[11px] font-display uppercase tracking-wider text-white/45"
            data-testid="home-progress"
          >
            <span>LVL {state.level}</span>
            <span className="opacity-40">·</span>
            <span>BEST {state.bestScore}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="hud-pill hud-pill--cyan cursor-pointer"
            onClick={() => { gameStore.toggleSound(); audio.start(); }}
            data-testid="sound-toggle"
            aria-label="Toggle sound"
          >
            {state.settings.sound
              ? <Volume2 className="w-5 h-5 text-[#00FFFF]" />
              : <VolumeX className="w-5 h-5 text-[#00FFFF]" />}
          </button>
          <button
            className="hud-pill hud-pill--pink cursor-pointer"
            onClick={() => setResetOpen(true)}
            data-testid="reset-progress-btn"
            aria-label="Reset progress"
          >
            <RotateCcw className="w-5 h-5 text-[#FF007F]" />
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="flex flex-col items-center gap-2 text-center px-4">
        <div className="anim-floaty">
          <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-none">
            <span className="neon-pink">CROSSBEAU</span>
            <br />
            <span className="neon-cyan">CHAOS</span>
          </h1>
        </div>
        <p className="mt-3 font-body text-base sm:text-lg text-white/80 max-w-md">
          Shoot silly targets. Collect coins. Unlock awesome gear.
        </p>
      </div>

      {/* Buttons — Play is the hero; Mini Games is a secondary action;
          Shop and Target Guide are quieter info utilities in a footer row.
          The loadout row above Play shows the currently equipped gear. */}
      <div className="w-full flex flex-col items-center gap-3 px-6 z-10 safe-bottom-lg safe-x">
        <EquippedLoadout state={state} />
        <button
          onClick={() => go("/play")}
          className="btn-neon btn-pink w-full max-w-sm text-xl anim-glow-pulse"
          data-testid="play-btn"
        >
          <Play className="w-6 h-6 fill-white" /> Play
        </button>
        <button
          onClick={() => go("/minigames")}
          className="btn-neon btn-yellow w-full max-w-sm"
          data-testid="minigames-btn"
        >
          <Gamepad2 className="w-5 h-5" /> Mini Games
        </button>
        <div className="flex gap-3 w-full max-w-sm">
          <button
            onClick={() => go("/shop/crossbow")}
            className="btn-neon btn-cyan flex-1"
            data-testid="shop-btn"
          >
            <ShoppingBag className="w-5 h-5" /> Shop
          </button>
          <button
            onClick={() => go("/targets")}
            className="btn-neon btn-pink flex-1"
            data-testid="targets-btn"
          >
            <BookOpen className="w-5 h-5" /> Targets
          </button>
        </div>
        <div className="flex gap-3 items-center">
          <button
            onClick={() => nav("/privacy")}
            className="mt-2 text-[10px] font-display uppercase tracking-[0.2em] text-white/40 hover:text-white/70 transition-colors"
            data-testid="privacy-link"
          >
            Privacy Policy
          </button>
          <button
            onClick={() => nav("/support")}
            className="mt-2 text-[10px] font-display uppercase tracking-[0.2em] text-white/40 hover:text-white/70 transition-colors"
            data-testid="support-link"
          >
            Support
          </button>
        </div>
      </div>

      <FloatingDeco />

      {/* Reset confirm */}
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent
          data-testid="reset-dialog"
          className="border-2 border-[#FF007F] shadow-[0_0_24px_rgba(255,0,127,0.4)] bg-[#14082e] text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl neon-pink">
              Reset everything?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-body text-white/80">
              This wipes all coins, unlocked gear, streaks, and level progress.
              You can&apos;t undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid="reset-cancel-btn"
              className="btn-neon btn-cyan border-2"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              data-testid="reset-confirm-btn"
              className="btn-neon btn-pink"
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EquippedLoadout({ state }) {
  const nav = useNavigate();
  const crossbow = CROSSBOWS.find((c) => c.id === state.equipped.crossbow);
  const arrow    = ARROWS.find((a) => a.id === state.equipped.arrow);
  const skin     = SKINS.find((s) => s.id === state.equipped.skin);
  const goShop = (tab) => {
    audio.unlock();
    audio.start();
    nav(`/shop/${tab}`);
  };
  return (
    <div
      className="flex items-start justify-center gap-3 sm:gap-4 w-full max-w-sm mb-6"
      data-testid="equipped-loadout"
    >
      <LoadoutCard
        label="Crossbow"
        item={crossbow}
        color="#00FFFF"
        onClick={() => goShop("crossbow")}
        testId="loadout-crossbow"
      />
      <LoadoutCard
        label="Arrow"
        item={arrow}
        color="#FFD700"
        arrowPreview
        onClick={() => goShop("arrow")}
        testId="loadout-arrow"
      />
      <LoadoutCard
        label="Skin"
        item={skin}
        color="#FF007F"
        onClick={() => goShop("skin")}
        testId="loadout-skin"
      />
    </div>
  );
}

function LoadoutCard({ label, item, color, arrowPreview, onClick, testId }) {
  const src = item && ASSETS[item.id];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex flex-col items-center gap-1 min-w-0 cursor-pointer group focus:outline-none"
    >
      <div
        className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-black/40 border-2 flex items-center justify-center overflow-hidden transition-all group-hover:-translate-y-0.5"
        style={{ borderColor: `${color}66`, boxShadow: `0 0 10px ${color}33` }}
      >
        {arrowPreview ? (
          <ArrowPreview color={item?.color} glow={item?.glow} />
        ) : src ? (
          <KeyedImage src={src} alt={item?.name || ""} className="w-full h-full object-contain p-1" />
        ) : item?.emoji ? (
          <span className="text-2xl" style={{ filter: `drop-shadow(0 0 8px ${color})` }}>
            {item.emoji}
          </span>
        ) : null}
      </div>
      <span
        className="text-[10px] font-display uppercase tracking-wider"
        style={{ color: `${color}C0` }}
      >
        {label}
      </span>
      <span className="text-[9px] text-white/60 max-w-[78px] truncate">
        {item?.name || "—"}
      </span>
    </button>
  );
}

function ArrowPreview({ color, glow }) {
  const rainbow = color === "rainbow";
  const fill = rainbow ? "url(#home-rainbow)" : color || "#E0E0E0";
  const shadow = glow || "#FFFFFF";
  return (
    <svg viewBox="0 0 80 30" className="w-10 h-6">
      <defs>
        <linearGradient id="home-rainbow" x1="0" x2="1">
          <stop offset="0%"   stopColor="#FF007F" />
          <stop offset="50%"  stopColor="#FFD700" />
          <stop offset="100%" stopColor="#00FFFF" />
        </linearGradient>
        <filter id="home-arrow-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g filter="url(#home-arrow-glow)" style={{ color: shadow }}>
        <rect x="8" y="13" width="48" height="4" fill={fill} rx="2" />
        <polygon points="56,8 72,15 56,22" fill={fill} />
        <polygon points="8,15 2,10 2,20" fill="#FF007F" />
      </g>
    </svg>
  );
}

// Targets eligible to appear as home-screen decoration (skip special ones).
const DECO_TARGET_IDS = TARGETS
  .map((t) => t.id)
  .filter((id) => id !== "target_skull" && id !== "target_trophy");

function pickInitialIds(n) {
  // Fisher-Yates shuffle, take first n so the initial four are unique.
  const pool = [...DECO_TARGET_IDS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(n, pool.length));
}

function FloatingDeco() {
  const slots = [
    { style: { top: "18%", left: "8%", width: 90 },   floatDelay: "0s" },
    { style: { top: "22%", right: "10%", width: 100 }, floatDelay: "1s" },
    { style: { bottom: "32%", left: "6%", width: 80 }, floatDelay: "0.5s" },
    { style: { bottom: "36%", right: "8%", width: 90 },floatDelay: "1.5s" },
  ];
  // Shared slot state so no two slots ever show the same target at once.
  const [ids, setIds] = useState(() => pickInitialIds(slots.length));
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const swapSlot = useCallback((slotIndex) => {
    const excluded = new Set(idsRef.current.filter((_, i) => i !== slotIndex));
    const pool = DECO_TARGET_IDS.filter((id) => !excluded.has(id));
    // Also avoid picking the same target the slot is already showing.
    const currentId = idsRef.current[slotIndex];
    const finalPool = pool.filter((id) => id !== currentId);
    const bucket = finalPool.length > 0 ? finalPool : pool;
    const next = bucket[Math.floor(Math.random() * bucket.length)];
    setIds((prev) => prev.map((v, i) => (i === slotIndex ? next : v)));
  }, []);

  return (
    <>
      {slots.map((s, i) => (
        <FloatingSlot
          key={i}
          targetId={ids[i]}
          slotStyle={s.style}
          floatDelay={s.floatDelay}
          onSwap={() => swapSlot(i)}
        />
      ))}
    </>
  );
}

function FloatingSlot({ slotStyle, floatDelay, targetId, onSwap }) {
  const [exploding, setExploding] = useState(false);
  const onSwapRef = useRef(onSwap);
  onSwapRef.current = onSwap;

  useEffect(() => {
    let swapTimer, endTimer;
    const schedule = () => {
      // staggered lifetime so all four don't explode in unison
      const lifetime = 4500 + Math.random() * 5000;
      swapTimer = setTimeout(() => {
        setExploding(true);
        endTimer = setTimeout(() => {
          onSwapRef.current();
          setExploding(false);
          schedule();
        }, 560);
      }, lifetime);
    };
    schedule();
    return () => {
      clearTimeout(swapTimer);
      clearTimeout(endTimer);
    };
  }, []);

  return (
    <div
      className="absolute pointer-events-none hidden sm:block"
      style={slotStyle}
    >
      <div className="anim-floaty relative" style={{ animationDelay: floatDelay }}>
        {exploding ? (
          <>
            <div className="anim-target-poof">
              <KeyedImage
                src={ASSETS[targetId]}
                alt=""
                style={{
                  width: "100%",
                  filter: "drop-shadow(0 0 24px #FFD700)",
                }}
              />
            </div>
            <BurstParticles />
          </>
        ) : (
          <div className="anim-pop-in" key={targetId}>
            <KeyedImage
              src={ASSETS[targetId]}
              alt=""
              style={{
                width: "100%",
                filter: "drop-shadow(0 0 14px rgba(0,255,255,0.4))",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function BurstParticles() {
  const count = 12;
  const palette = ["#FFD700", "#FF007F", "#00FFFF", "#39FF14"];
  const particles = Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.3;
    const dist = 55 + Math.random() * 30;
    return {
      color: palette[i % palette.length],
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist,
    };
  });
  return (
    <div className="absolute inset-0">
      {particles.map((p, i) => (
        <span
          key={i}
          className="burst-particle"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: 10,
            height: 10,
            marginLeft: -5,
            marginTop: -5,
            borderRadius: "50%",
            background: p.color,
            boxShadow: `0 0 14px ${p.color}`,
            "--dx": `${p.dx}px`,
            "--dy": `${p.dy}px`,
          }}
        />
      ))}
    </div>
  );
}
